import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { getHandler } from '../lib/actions/registry'
import type { ActionType, ActionPayloadMap } from '../lib/actions/types'

export function useActionDispatch(characterId: string, campaignId: string) {
  const queryClient = useQueryClient()

  const dispatch = useCallback(async <T extends ActionType>(
    actionType: T,
    payload: ActionPayloadMap[T],
    opts?: { groupId?: string }
  ): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const handler = getHandler(actionType)
    const ctx = { characterId, campaignId, supabase }

    // 1. Run handler — applies mutation, returns effects
    const result = await handler(ctx, payload)

    // 2. Get game year from campaign cache
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const campaign = queryClient.getQueryData<any>(['campaign', campaignId])
    const gameYear = campaign?.current_tr_year ?? null

    // 3. Insert action row
    const { data: action, error: actionErr } = await supabase
      .from('actions')
      .insert({
        character_id: characterId,
        performed_by: user.id,
        group_id: opts?.groupId ?? null,
        action_type: actionType,
        label: result.label,
        description: result.description ?? null,
        game_year: gameYear,
        payload,
        undo_policy: result.undoPolicy ?? 'undoable',
      })
      .select('id')
      .single()

    if (actionErr) throw actionErr

    // 4. Batch insert effects
    if (result.effects.length > 0) {
      const effectRows = result.effects.map(e => ({
        action_id: action.id,
        character_id: e.character_id,
        target_table: e.target_table,
        target_row_id: e.target_row_id,
        target_field: e.target_field,
        operation: e.operation,
        old_value: e.old_value,
        new_value: e.new_value,
      }))

      const { error: effectErr } = await supabase
        .from('effects')
        .insert(effectRows)

      if (effectErr) {
        console.error('[Action] Effects insert failed:', effectErr)
        // Data mutation already applied — action payload is the recovery source
      }
    }

    // 5. Invalidate caches
    queryClient.invalidateQueries({ queryKey: ['character', characterId] })

    return action.id
  }, [characterId, campaignId, queryClient])

  return { dispatch }
}
